import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  ClipboardList,
  StickyNote,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  PenLine,
  Download,
} from 'lucide-react';
import {
  formatDateForDisplay,
  formatDateToISO,
  formatDateToZeroPaddedMMDDYYYY,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { getDefaultHotboxDateRange } from '../utils/hotbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Button,
  Badge,
  DatePicker,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@roger/ui-components';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  exportQaAllTasksTasksMyTasksExportGet,
  getMyTasksTasksMyTasksGet,
  getMyTasksTypeOptionsTasksMyTasksTypeOptionsGet,
  rescheduleTaskTasksTaskIdReschedulePatch,
  updateTaskNotesTasksTaskIdNotesPut,
} from '../services/api/sdk.gen';
import type {
  GetMyTasksTasksMyTasksGetData,
  HotboxTaskResponse,
  HotboxTaskTypeOptionResponse,
  TaskNotesResponse,
} from '../services/api/types.gen';
import { useCanRescheduleFromHotbox } from '../hooks/useCanRescheduleFromHotbox';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { downloadResponseAsFile } from '../utils/downloadFile';
import type { VisitTypeId } from '../services/api/types.gen';
import { EVVCAPTURESTATE_VALUES } from '../services/api/models/EvvCaptureState';
import type { SortBy } from '../services/api/models/SortBy';
import type { SortDirection } from '../services/api/models/SortDirection';
import { supportsActiveDiagnosisPage } from '../constants/visitTypes';
import {
  ALL_CLINICS,
  useAccessibleClinics,
} from '../hooks/useAccessibleClinics';
import { ClinicSelect } from '../components/clinic/ClinicSelect';
import { HotboxEvvCaptureDialog } from '../components/hotbox/HotboxEvvCaptureDialog';
import { HotboxNotesCell } from '../components/hotbox/HotboxNotesCell';
import { HotboxTaskNotesDialog } from '../components/hotbox/HotboxTaskNotesDialog';
import { EvvReadingsDialog } from '../components/visit/EvvReadingsDialog';
import { getDefaultLandingPath } from '../utils/defaultLandingPath';
import { PermissionSlug } from '../constants/featureFlags';

type HotboxTaskType = NonNullable<
  NonNullable<GetMyTasksTasksMyTasksGetData['query']>['task_types']
>[number];

type RoleFilter = 'all' | 'clinician' | 'case_manager';
type QAScope = 'all' | 'my';
type HotboxScope = 'clinician' | 'qa';

const PAGE_SIZE = 50;

const TASK_TYPE_OPTIONS: { value: HotboxTaskType; label: string }[] = [
  { value: 'visit', label: 'Visits' },
  { value: 'order', label: 'Orders' },
  { value: 'patient_communication', label: 'Communications' },
  { value: 'misc_task', label: 'Misc Tasks' },
];

// Clinicians are used to a visits-only queue and find the other subtypes noisy,
// so the filter starts there on every load and is not persisted.
const DEFAULT_TASK_TYPES: HotboxTaskType[] = ['visit'];

function taskTypeFilterLabel(taskTypes: HotboxTaskType[]): string {
  // Not "All Tasks": the QA scope selector next to it already uses that label.
  if (taskTypes.length === TASK_TYPE_OPTIONS.length) return 'All Task Types';
  if (taskTypes.length === 1) {
    return (
      TASK_TYPE_OPTIONS.find(option => option.value === taskTypes[0])?.label ??
      '1 type'
    );
  }
  return `${taskTypes.length} types`;
}
type HotboxStatus = NonNullable<
  NonNullable<GetMyTasksTasksMyTasksGetData['query']>['statuses']
>[number];

// Mirrors TaskCRUD._CLINICIAN_STATUSES / _CASE_MANAGER_STATUSES: each scope
// only ever shows its own statuses, so the filter offers exactly those.
const CLINICIAN_STATUS_OPTIONS: HotboxStatus[] = [
  'not_started',
  'saved',
  'returned_for_review',
  'pending_clinician_signature',
];
const QA_STATUS_OPTIONS: HotboxStatus[] = [
  'submitted_with_signature',
  'submitted',
];

type EvvFilter = 'all' | 'captured' | 'not_captured';

const EVV_FILTER_OPTIONS: { value: EvvFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'captured', label: 'Captured' },
  { value: 'not_captured', label: 'Not captured' },
];

function statusFilterLabel(statuses: HotboxStatus[]): string {
  if (statuses.length === 0) return 'All Statuses';
  if (statuses.length === 1) return STATUS_LABEL[statuses[0]] ?? statuses[0];
  return `${statuses.length} statuses`;
}

function typeFilterLabel(visitTypes: string[]): string {
  if (visitTypes.length === 0) return 'All Types';
  if (visitTypes.length === 1) return formatVisitType(visitTypes[0]);
  return `${visitTypes.length} types`;
}

const REVIEWABLE_EVV_STATES = new Set(EVVCAPTURESTATE_VALUES);
REVIEWABLE_EVV_STATES.delete('not_started');

const DEFAULT_SORT_DIRECTION: Record<SortBy, SortDirection> = {
  visit_date: 'desc',
  status: 'desc',
  patient_name: 'asc',
  clinic_name: 'asc',
  visit_type: 'asc',
  assigned: 'asc',
};

type SortableColumn = {
  column: SortBy;
  label: string;
  className: string;
  allClinicsOnly?: boolean;
};

const SORTABLE_COLUMNS: SortableColumn[] = [
  { column: 'visit_date', label: 'Date', className: 'w-[120px]' },
  { column: 'patient_name', label: 'Patient', className: 'w-48' },
  {
    column: 'clinic_name',
    label: 'Clinic',
    className: 'w-40',
    allClinicsOnly: true,
  },
  { column: 'visit_type', label: 'Type', className: 'w-40' },
  { column: 'status', label: 'Status', className: 'w-40' },
  { column: 'assigned', label: 'Assigned', className: 'w-56' },
];

const TASK_NOTES_SAVE_ERROR = 'Failed to save task notes.';

type ConfirmedTaskOverlay = {
  fields: Partial<HotboxTaskResponse>;
  throughFetchId: number;
};

function normalizeNotes(notes: string | null | undefined): string {
  return notes ?? '';
}

function hasGeneratedClientError(response: { error?: unknown }): boolean {
  return 'error' in response && response.error != null;
}

function assignmentLabel(task: HotboxTaskResponse): string {
  if (
    task.is_case_manager_task &&
    !task.is_clinician_task &&
    task.case_manager_name
  ) {
    return task.case_manager_name;
  }

  return task.assigned_clinician_name || task.case_manager_name || '—';
}

const STATUS_BADGE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  not_started: 'outline',
  saved: 'secondary',
  returned_for_review: 'destructive',
  submitted: 'default',
  submitted_with_signature: 'default',
  pending_clinician_signature: 'destructive',
  completed: 'secondary',
};

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not Started',
  saved: 'Saved',
  returned_for_review: 'Returned for Review',
  submitted: 'Submitted',
  submitted_with_signature: 'Submitted (Signed)',
  pending_clinician_signature: 'Pending Signature',
  completed: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Pending',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
};

function formatVisitType(visitType: string): string {
  return visitType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bOasis\b/i, 'OASIS')
    .replace(/\bSoc\b/i, 'SOC')
    .replace(/\bRoc\b/i, 'ROC')
    .replace(/\bRn\b/i, 'RN')
    .replace(/\bLpn\b/i, 'LPN')
    .replace(/\bPt\b/i, 'PT')
    .replace(/\bOt\b/i, 'OT')
    .replace(/\bSt\b/i, 'ST')
    .replace(/\bMsw\b/i, 'MSW')
    .replace(/\bHha\b/i, 'HHA');
}

type SortableTableHeadProps = {
  column: SortBy;
  sortField: SortBy;
  sortDirection: SortDirection;
  onSort: (column: SortBy) => void;
  className?: string;
  children: React.ReactNode;
};

const SortableTableHead: React.FC<SortableTableHeadProps> = ({
  column,
  sortField,
  sortDirection,
  onSort,
  className,
  children,
}) => (
  <TableHead className={className}>
    <button
      type='button'
      className='flex w-full items-center gap-1 text-left font-medium hover:text-foreground'
      onClick={() => onSort(column)}
    >
      <span>{children}</span>
      {sortField === column ? (
        sortDirection === 'asc' ? (
          <ArrowUp className='h-3.5 w-3.5 shrink-0' />
        ) : (
          <ArrowDown className='h-3.5 w-3.5 shrink-0' />
        )
      ) : (
        <ArrowDown className='h-3.5 w-3.5 shrink-0 opacity-30' />
      )}
    </button>
  </TableHead>
);

const Hotbox: React.FC = () => {
  const navigate = useNavigate();
  const {
    showError,
    showSuccess,
    user,
    hasPermission,
    hasAnyPermission,
    hasFeatureFlag,
  } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
      user: state.user,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const canReschedule = useCanRescheduleFromHotbox();

  useEffect(() => {
    if (
      !hasPermission(PermissionSlug.EMR_MANAGE) &&
      !hasPermission(PermissionSlug.HOTBOX_ACCESS) &&
      !hasPermission(PermissionSlug.QA_ACCESS)
    ) {
      navigate(
        getDefaultLandingPath({
          hasPermission,
          hasAnyPermission,
          hasFeatureFlag,
        })
      );
    }
  }, [hasAnyPermission, hasFeatureFlag, hasPermission, navigate]);

  const userClinics = user?.user_clinics ?? [];

  const {
    clinics: clinicOptions,
    selectedClinicId,
    setSelectedClinicId,
    isAllClinics,
    shouldShowPicker,
    clinicsLoaded,
  } = useAccessibleClinics({
    requireAssignment: true,
    persistKey: 'hotbox',
  });

  const [tasks, setTasks] = useState<HotboxTaskResponse[]>([]);
  // Per task, not one global flag: rows save independently, so picking a date
  // on a second row while the first is still in flight sends its own request
  // instead of being silently dropped.
  const [reschedulingTaskIds, setReschedulingTaskIds] = useState<Set<string>>(
    () => new Set()
  );
  // Bumped per task when a save is rejected, so only that row's picker remounts
  // and snaps back to the server's date instead of keeping the value the user
  // just picked. Keyed by task id so a rejection on one row does not tear down
  // an open calendar on another.
  const [rejectedRescheduleNonces, setRejectedRescheduleNonces] = useState<
    Record<string, number>
  >({});
  const [loading, setLoading] = useState(true);
  const [activeScope, setActiveScope] = useState<HotboxScope>('clinician');
  const [qaScope, setQAScope] = useState<QAScope>('all');
  const [isQAScopeMenuOpen, setIsQAScopeMenuOpen] = useState(false);
  const [taskTypes, setTaskTypes] =
    useState<HotboxTaskType[]>(DEFAULT_TASK_TYPES);
  // Empty selections mean "no filter" for all three column filters (EMR-1305).
  const [statusFilter, setStatusFilter] = useState<HotboxStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [evvFilter, setEvvFilter] = useState<EvvFilter>('all');
  const [typeOptions, setTypeOptions] = useState<
    HotboxTaskTypeOptionResponse[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<SortBy>('visit_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    () => getDefaultHotboxDateRange().from
  );
  const [dateTo, setDateTo] = useState<Date | undefined>(
    () => getDefaultHotboxDateRange().to
  );
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [evvCaptureVisitId, setEvvCaptureVisitId] = useState<string | null>(
    null
  );
  const [evvReadVisitId, setEvvReadVisitId] = useState<string | null>(null);
  const [evvRefreshNonce, setEvvRefreshNonce] = useState(0);
  const [confirmedNotes, setConfirmedNotes] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [taskNotesError, setTaskNotesError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const fetchIdRef = useRef(0);
  const confirmedTaskOverlayRef = useRef<Map<string, ConfirmedTaskOverlay>>(
    new Map()
  );

  const selectedUserClinic = useMemo(
    () =>
      isAllClinics
        ? undefined
        : clinicOptions.find(c => c.id === selectedClinicId)?.userClinic,
    [clinicOptions, selectedClinicId, isAllClinics]
  );

  const aggregateRoles = useMemo(() => {
    return {
      isClinician: userClinics.some(uc => uc.is_assignable_clinician),
      isCaseManager: userClinics.some(uc => uc.is_case_manager),
    };
  }, [userClinics]);

  const isClinicianScopeAvailable = isAllClinics
    ? aggregateRoles.isClinician
    : (selectedUserClinic?.is_assignable_clinician ?? false);
  const isCaseManagerScopeAvailable = isAllClinics
    ? aggregateRoles.isCaseManager
    : (selectedUserClinic?.is_case_manager ?? false);
  const isQAScopeAvailable =
    hasPermission(PermissionSlug.QA_ACCESS) || isCaseManagerScopeAvailable;
  const hasAnyRole = isClinicianScopeAvailable || isQAScopeAvailable;
  const canCaptureEvv = hasPermission(PermissionSlug.EVV_CAPTURE);
  const canViewEvv = hasPermission(PermissionSlug.EVV_VIEW);
  const showEvvColumn = canCaptureEvv || canViewEvv;
  // The column only earns its space when there is more than one clinic to
  // tell apart. A specific selection already hides it. A single-clinic user
  // has no picker, so they cannot change whatever selection they arrived
  // with; when that selection is "all clinics" (the usual case — a persisted
  // pick of their one clinic is the exception), every row would still name
  // the same clinic, so the column must go too.
  // Until the clinic list has loaded the count is unknown, so keep the wider
  // layout (and hold task rows, below) rather than snapping a column in once
  // the fetch resolves for the multi-clinic majority.
  const showClinicColumn = isAllClinics && (!clinicsLoaded || shouldShowPicker);
  const tableColumnCount = (showClinicColumn ? 7 : 6) + (showEvvColumn ? 1 : 0);
  const roleFilter: RoleFilter =
    activeScope === 'qa' ? 'case_manager' : 'clinician';
  const emptyStateTaskNoun =
    taskTypes.length === 1 && taskTypes[0] === 'visit' ? 'visits' : 'tasks';

  const isDateRangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  function handleSort(column: SortBy) {
    if (sortField === column) {
      setSortDirection(currentDirection =>
        currentDirection === 'asc' ? 'desc' : 'asc'
      );
      return;
    }

    setSortField(column);
    setSortDirection(DEFAULT_SORT_DIRECTION[column]);
  }

  // Remember the current list fetch generation so a stale response cannot make
  // a just-saved field look unsaved. Fields merge, so a note and a date
  // confirmed on the same row both survive the same in-flight GET.
  const rememberConfirmedTask = useCallback(
    (taskId: string, fields: Partial<HotboxTaskResponse>) => {
      const overlayMap = confirmedTaskOverlayRef.current;
      const existing = overlayMap.get(taskId);
      overlayMap.set(taskId, {
        fields: { ...existing?.fields, ...fields },
        throughFetchId: fetchIdRef.current,
      });
    },
    []
  );

  const applyConfirmedTaskOverlay = useCallback(
    (fetchedTasks: HotboxTaskResponse[], fetchId: number) => {
      // Saving a task can race an already-started list refresh. Keep the
      // confirmed write response visible until that in-flight GET has had one
      // chance to settle, then trust the next server snapshot again.
      const overlayMap = confirmedTaskOverlayRef.current;
      if (overlayMap.size === 0) return fetchedTasks;

      const tasksWithOverlay = fetchedTasks.map(task => {
        const overlay = overlayMap.get(task.id);
        if (!overlay) return task;
        if (fetchId > overlay.throughFetchId) {
          overlayMap.delete(task.id);
          return task;
        }
        if (fetchId === overlay.throughFetchId) {
          overlayMap.delete(task.id);
        }
        return { ...task, ...overlay.fields };
      });

      for (const [taskId, overlay] of overlayMap) {
        if (fetchId > overlay.throughFetchId) {
          overlayMap.delete(taskId);
        }
      }

      return tasksWithOverlay;
    },
    []
  );

  useEffect(() => {
    if (activeScope === 'clinician' && !isClinicianScopeAvailable) {
      setActiveScope(isQAScopeAvailable ? 'qa' : 'clinician');
      return;
    }
    if (activeScope === 'qa' && !isQAScopeAvailable) {
      setActiveScope(isClinicianScopeAvailable ? 'clinician' : 'qa');
    }
  }, [activeScope, isClinicianScopeAvailable, isQAScopeAvailable]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // The two scopes show disjoint status sets, so a selection made in one is
  // meaningless in the other. Keep the same array identity when already empty
  // so a scope switch doesn't refire the list fetch a second time.
  useEffect(() => {
    setStatusFilter(current => (current.length === 0 ? current : []));
  }, [activeScope]);

  useEffect(() => {
    if (isDateRangeInvalid) {
      setTypeOptions([]);
      return;
    }

    let cancelled = false;
    getMyTasksTypeOptionsTasksMyTasksTypeOptionsGet({
      query: {
        role_filter: roleFilter,
        qa_scope: activeScope === 'qa' ? qaScope : 'my',
        task_types: taskTypes,
        // Status and EVV narrow the options too, so the menu never offers a
        // type the filtered list cannot contain. The type filter itself and
        // the name search stay out (see get_user_task_type_options).
        statuses: statusFilter.length > 0 ? statusFilter : undefined,
        evv_present: evvFilter === 'all' ? undefined : evvFilter === 'captured',
        clinic_id: isAllClinics ? undefined : selectedClinicId || undefined,
        date_from: dateFrom ? formatDateToISO(dateFrom) : undefined,
        date_to: dateTo ? formatDateToISO(dateTo) : undefined,
      },
    })
      .then(response => {
        if (cancelled) return;
        const options = Array.isArray(response.data) ? response.data : [];
        setTypeOptions(options);
        // Drop selections the queue can no longer produce (e.g. a visit type
        // picked before switching to Orders), so a stale pick doesn't silently
        // filter everything out.
        setTypeFilter(current => {
          const next = current.filter(value =>
            options.some(option => option.visit_type === value)
          );
          return next.length === current.length ? current : next;
        });
      })
      .catch(error => {
        // The dropdown keeps its previous options; the list request surfaces
        // real connectivity problems.
        if (cancelled) return;
        console.error('Failed to load Hotbox type options', error);
      });
    return () => {
      cancelled = true;
    };
  }, [
    roleFilter,
    activeScope,
    qaScope,
    taskTypes,
    statusFilter,
    evvFilter,
    selectedClinicId,
    isAllClinics,
    dateFrom,
    dateTo,
    isDateRangeInvalid,
  ]);

  useEffect(() => {
    setPage(0);
  }, [
    activeScope,
    qaScope,
    taskTypes,
    statusFilter,
    typeFilter,
    evvFilter,
    debouncedSearch,
    selectedClinicId,
    sortField,
    sortDirection,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    if (isDateRangeInvalid) {
      setLoading(false);
      setTasks([]);
      setHasMore(false);
      return;
    }

    const id = ++fetchIdRef.current;
    setLoading(true);

    getMyTasksTasksMyTasksGet({
      query: {
        role_filter: roleFilter,
        qa_scope: activeScope === 'qa' ? qaScope : 'my',
        task_types: taskTypes,
        statuses: statusFilter.length > 0 ? statusFilter : undefined,
        visit_types: typeFilter.length > 0 ? typeFilter : undefined,
        evv_present: evvFilter === 'all' ? undefined : evvFilter === 'captured',
        search: debouncedSearch || '',
        clinic_id: isAllClinics ? undefined : selectedClinicId || undefined,
        date_from: dateFrom ? formatDateToISO(dateFrom) : undefined,
        date_to: dateTo ? formatDateToISO(dateTo) : undefined,
        sort_by: sortField,
        sort_direction: sortDirection,
        limit: PAGE_SIZE + 1,
        offset: page * PAGE_SIZE,
      },
    })
      .then(response => {
        if (id !== fetchIdRef.current) return;
        const data = Array.isArray(response.data) ? response.data : [];
        setHasMore(data.length > PAGE_SIZE);
        setTasks(applyConfirmedTaskOverlay(data.slice(0, PAGE_SIZE), id));
      })
      .catch(() => {
        if (id !== fetchIdRef.current) return;
        setTasks([]);
        showError('Failed to load tasks');
      })
      .finally(() => {
        if (id === fetchIdRef.current) {
          setLoading(false);
        }
      });
  }, [
    roleFilter,
    activeScope,
    qaScope,
    taskTypes,
    statusFilter,
    typeFilter,
    evvFilter,
    debouncedSearch,
    selectedClinicId,
    isAllClinics,
    page,
    sortField,
    sortDirection,
    dateFrom,
    dateTo,
    evvRefreshNonce,
    isDateRangeInvalid,
    showError,
    applyConfirmedTaskOverlay,
  ]);

  const handleRowClick = (task: HotboxTaskResponse, e: React.MouseEvent) => {
    const path = `/patients/${task.patient_id}/visits/${task.id}`;
    if (e.metaKey || e.ctrlKey) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  const resetTaskNotesDialog = () => {
    setSelectedTaskId(null);
    setConfirmedNotes('');
    setDraftNotes('');
    setTaskNotesError(null);
  };

  const isReviewableEvvTask = (task: HotboxTaskResponse) =>
    task.has_evv_data ||
    (task.evv_capture_state != null &&
      REVIEWABLE_EVV_STATES.has(task.evv_capture_state));

  const canOpenEvvAction = (task: HotboxTaskResponse) =>
    isReviewableEvvTask(task) ? canViewEvv || canCaptureEvv : canCaptureEvv;

  const openEvvAction = (
    task: HotboxTaskResponse,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    if (isReviewableEvvTask(task)) {
      setEvvReadVisitId(task.id);
      return;
    }
    setEvvCaptureVisitId(task.id);
  };

  const openTaskNotesDialog = (
    task: HotboxTaskResponse,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    const notes = normalizeNotes(task.notes);
    setSelectedTaskId(task.id);
    setConfirmedNotes(notes);
    setDraftNotes(notes);
    setTaskNotesError(null);
  };

  const handleRescheduleTask = async (
    task: HotboxTaskResponse,
    newDate: Date | undefined
  ) => {
    const newDateIso = newDate ? formatDateToISO(newDate) : null;
    if (!newDateIso || newDateIso === task.visit_date) {
      return;
    }
    if (reschedulingTaskIds.has(task.id)) {
      return;
    }

    setReschedulingTaskIds(current => new Set(current).add(task.id));
    try {
      const response = await rescheduleTaskTasksTaskIdReschedulePatch({
        path: { task_id: task.id },
        body: { new_date: newDateIso },
      });

      if (hasGeneratedClientError(response) || !response.data) {
        throw response;
      }

      const updatedDate = response.data.visit_date;
      rememberConfirmedTask(task.id, { visit_date: updatedDate });
      setTasks(currentTasks =>
        currentTasks.map(row =>
          row.id === task.id ? { ...row, visit_date: updatedDate } : row
        )
      );
      // The row is patched in place and deliberately not re-sorted or
      // refetched — a row jumping out from under the cursor is what reads as
      // broken. But the default view ends at today, so a task moved forward
      // leaves the list on the next load. Say so now, or its later
      // disappearance looks like the save never stuck.
      const movedDate = parseISOToLocalDate(updatedDate);
      const leftTheFilter = Boolean(
        movedDate &&
          ((dateFrom && movedDate < dateFrom) || (dateTo && movedDate > dateTo))
      );
      showSuccess(
        leftTheFilter
          ? `Moved to ${formatDateForDisplay(updatedDate)} — outside the current date filter`
          : 'Task rescheduled'
      );
    } catch (error) {
      console.error('Failed to reschedule task', error);
      // The picker kept the date the user chose; remount it so the row shows
      // what the server still holds.
      setRejectedRescheduleNonces(current => ({
        ...current,
        [task.id]: (current[task.id] ?? 0) + 1,
      }));
      showError(getBusinessErrorMessage(error) ?? 'Failed to reschedule task');
    } finally {
      setReschedulingTaskIds(current => {
        const next = new Set(current);
        next.delete(task.id);
        return next;
      });
    }
  };

  const handleSaveTaskNotes = async () => {
    if (!selectedTaskId || isSavingNotes || draftNotes === confirmedNotes) {
      return;
    }

    setIsSavingNotes(true);
    setTaskNotesError(null);

    try {
      const response = await updateTaskNotesTasksTaskIdNotesPut({
        path: { task_id: selectedTaskId },
        body: { notes: draftNotes === '' ? null : draftNotes },
      });

      if (hasGeneratedClientError(response) || !response.data) {
        throw response;
      }

      const data: TaskNotesResponse = response.data;
      rememberConfirmedTask(data.id, {
        notes: data.notes,
        status: data.status,
      });
      setTasks(currentTasks =>
        currentTasks.map(task =>
          task.id === data.id
            ? { ...task, notes: data.notes, status: data.status }
            : task
        )
      );
      resetTaskNotesDialog();
    } catch (error) {
      console.error('Failed to save task notes', error);
      setTaskNotesError(TASK_NOTES_SAVE_ERROR);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleClinicChange = (clinicId: string) => {
    setSelectedClinicId(clinicId);
    setSearchTerm('');
  };

  const canExportQaAll = activeScope === 'qa' && qaScope === 'all';

  const handleExportQaAll = async () => {
    if (!canExportQaAll || isExporting || isDateRangeInvalid) return;

    setIsExporting(true);
    try {
      const response = await exportQaAllTasksTasksMyTasksExportGet({
        query: {
          role_filter: 'case_manager',
          qa_scope: 'all',
          task_types: taskTypes,
          statuses: statusFilter.length > 0 ? statusFilter : undefined,
          visit_types: typeFilter.length > 0 ? typeFilter : undefined,
          evv_present:
            evvFilter === 'all' ? undefined : evvFilter === 'captured',
          search: debouncedSearch || '',
          clinic_id: isAllClinics ? undefined : selectedClinicId || undefined,
          // Mirror the table: the workbook drops the Clinic column whenever
          // the queue only ever names one clinic.
          include_clinic: showClinicColumn,
          date_from: dateFrom ? formatDateToISO(dateFrom) : undefined,
          date_to: dateTo ? formatDateToISO(dateTo) : undefined,
          sort_by: sortField,
          sort_direction: sortDirection,
        },
        responseType: 'blob' as const,
      });

      if (response.error || !response.data) {
        console.error('Failed to export QA All Tasks', response);
        showError(
          await getUserFacingApiMessageAsync(
            response,
            'Unable to export QA tasks. Please try again.'
          )
        );
        return;
      }

      downloadResponseAsFile(response, 'Roger_QA_All_Tasks.xlsx');
    } catch (error) {
      console.error('Failed to export QA All Tasks', error);
      showError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to export QA tasks. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasAnyRole) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <ClipboardList className='h-6 w-6 text-primary' />
          <h1 className='text-2xl font-bold'>Hotbox</h1>
        </div>
        <div className='rounded-md border p-12 text-center text-muted-foreground'>
          <ClipboardList className='mx-auto mb-3 h-10 w-10 text-muted-foreground/50' />
          <p className='text-lg font-medium'>No tasks to show</p>
          <p className='mt-1 text-sm'>
            The Hotbox displays clinician and QA tasks. You are not currently
            assigned a supported Hotbox role at any clinic.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <ClipboardList className='h-6 w-6 text-primary' />
          <h1 className='text-2xl font-bold'>Hotbox</h1>
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        {shouldShowPicker && (
          <ClinicSelect
            className='w-56'
            selectSize='sm'
            clinics={clinicOptions}
            includeAllOption
            allOptionValue={ALL_CLINICS}
            value={selectedClinicId}
            onChange={e => handleClinicChange(e.target.value)}
            placeholder='Select clinic...'
          />
        )}

        <div className='relative'>
          <Search className='absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search by patient name...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='pl-9 w-full md:w-72'
            inputSize='sm'
          />
        </div>

        {(isClinicianScopeAvailable || isQAScopeAvailable) && (
          <div className='flex h-7 overflow-visible rounded-md border border-border'>
            {isClinicianScopeAvailable && (
              <button
                type='button'
                onClick={() => {
                  setActiveScope('clinician');
                  setIsQAScopeMenuOpen(false);
                }}
                className={`flex items-center px-2 text-xs font-medium transition-colors ${
                  activeScope === 'clinician'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-accent text-foreground'
                } ${isQAScopeAvailable ? 'rounded-l-md' : 'rounded-md'}`}
              >
                Clinician
              </button>
            )}

            {isQAScopeAvailable && (
              <div className='relative flex'>
                <button
                  type='button'
                  onClick={() => setActiveScope('qa')}
                  className={`flex items-center px-2 text-xs font-medium transition-colors ${
                    activeScope === 'qa'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent text-foreground'
                  } ${isClinicianScopeAvailable ? '' : 'rounded-l-md'}`}
                >
                  {qaScope === 'all' ? 'QA: All Tasks' : 'QA: My Tasks'}
                </button>
                <button
                  type='button'
                  aria-label='Choose QA task scope'
                  aria-haspopup='menu'
                  aria-expanded={isQAScopeMenuOpen}
                  onClick={() => {
                    setActiveScope('qa');
                    setIsQAScopeMenuOpen(open => !open);
                  }}
                  className={`flex items-center rounded-r-md border-l border-border px-1 transition-colors ${
                    activeScope === 'qa'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-accent text-foreground'
                  }`}
                >
                  <ChevronDown className='h-3 w-3' />
                </button>

                {isQAScopeMenuOpen && (
                  <div
                    role='menu'
                    className='absolute right-0 top-full z-20 mt-1 min-w-24 rounded-md border bg-popover p-1 text-popover-foreground shadow-md'
                  >
                    {[
                      { label: 'QA: All Tasks', value: 'all' as const },
                      { label: 'QA: My Tasks', value: 'my' as const },
                    ].map(option => (
                      <button
                        key={option.value}
                        type='button'
                        role='menuitemradio'
                        aria-checked={qaScope === option.value}
                        onClick={() => {
                          setActiveScope('qa');
                          setQAScope(option.value);
                          setIsQAScopeMenuOpen(false);
                        }}
                        className={`block w-full rounded-sm px-2 py-1 text-left text-xs hover:bg-accent ${
                          qaScope === option.value ? 'font-semibold' : ''
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              className='h-7 gap-1 px-2 text-xs font-medium'
            >
              {taskTypeFilterLabel(taskTypes)}
              <ChevronDown className='h-3 w-3' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start' className='min-w-40'>
            {TASK_TYPE_OPTIONS.map(option => {
              const isChecked = taskTypes.includes(option.value);
              // Unchecking the last type would ask the server for nothing, so
              // the final selection stays locked until another type is added.
              const isLastChecked = isChecked && taskTypes.length === 1;
              return (
                <DropdownMenuCheckboxItem
                  key={option.value}
                  checked={isChecked}
                  disabled={isLastChecked}
                  // Keep the menu open: picking one type is rarely the whole
                  // edit, and reopening after every click is tedious.
                  onSelect={event => event.preventDefault()}
                  onCheckedChange={() =>
                    setTaskTypes(current =>
                      current.includes(option.value)
                        ? current.filter(value => value !== option.value)
                        : TASK_TYPE_OPTIONS.filter(
                            candidate =>
                              candidate.value === option.value ||
                              current.includes(candidate.value)
                          ).map(candidate => candidate.value)
                    )
                  }
                  className='text-xs'
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              className='h-7 gap-1 px-2 text-xs font-medium'
            >
              {statusFilterLabel(statusFilter)}
              <ChevronDown className='h-3 w-3' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start' className='min-w-40'>
            {(activeScope === 'qa'
              ? QA_STATUS_OPTIONS
              : CLINICIAN_STATUS_OPTIONS
            ).map(statusOption => (
              <DropdownMenuCheckboxItem
                key={statusOption}
                checked={statusFilter.includes(statusOption)}
                onSelect={event => event.preventDefault()}
                onCheckedChange={() =>
                  setStatusFilter(current =>
                    current.includes(statusOption)
                      ? current.filter(value => value !== statusOption)
                      : [...current, statusOption]
                  )
                }
                className='text-xs'
              >
                {STATUS_LABEL[statusOption] ?? statusOption}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              className='h-7 gap-1 px-2 text-xs font-medium'
            >
              {typeFilterLabel(typeFilter)}
              <ChevronDown className='h-3 w-3' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='start'
            className='max-h-72 min-w-40 overflow-y-auto'
          >
            {typeOptions.length === 0 ? (
              <div className='px-2 py-1.5 text-xs text-muted-foreground'>
                No types in view
              </div>
            ) : (
              typeOptions.map(option => (
                <DropdownMenuCheckboxItem
                  key={`${option.task_type}-${option.visit_type}`}
                  checked={typeFilter.includes(option.visit_type)}
                  onSelect={event => event.preventDefault()}
                  onCheckedChange={() =>
                    setTypeFilter(current =>
                      current.includes(option.visit_type)
                        ? current.filter(value => value !== option.visit_type)
                        : [...current, option.visit_type]
                    )
                  }
                  className='text-xs'
                >
                  {formatVisitType(option.visit_type)}
                </DropdownMenuCheckboxItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {showEvvColumn && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outline'
                className='h-7 gap-1 px-2 text-xs font-medium'
              >
                {evvFilter === 'all'
                  ? 'Pt Sig: All'
                  : `Pt Sig: ${
                      EVV_FILTER_OPTIONS.find(
                        option => option.value === evvFilter
                      )?.label ?? evvFilter
                    }`}
                <ChevronDown className='h-3 w-3' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' className='min-w-40'>
              <DropdownMenuRadioGroup
                value={evvFilter}
                onValueChange={value => setEvvFilter(value as EvvFilter)}
              >
                {EVV_FILTER_OPTIONS.map(option => (
                  <DropdownMenuRadioItem
                    key={option.value}
                    value={option.value}
                    className='text-xs'
                  >
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DatePicker
          datePickerSize='sm'
          showClearButton
          placeholder='From date'
          date={dateFrom}
          onDateChange={setDateFrom}
          className='w-full md:w-[101px]'
        />

        <DatePicker
          datePickerSize='sm'
          showClearButton
          placeholder='To date'
          date={dateTo}
          onDateChange={setDateTo}
          className='w-full md:w-[101px]'
        />

        {canExportQaAll && (
          <Button
            variant='outline'
            size='sm'
            onClick={() => void handleExportQaAll()}
            disabled={isExporting || isDateRangeInvalid}
          >
            <Download className='h-4 w-4' />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        )}

        {loading && (
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        )}
      </div>

      <div className='overflow-x-auto rounded-md border'>
        <Table className='min-w-[980px]'>
          <TableHeader>
            <TableRow>
              {SORTABLE_COLUMNS.filter(
                column => !column.allClinicsOnly || showClinicColumn
              ).map(column => (
                <SortableTableHead
                  key={column.column}
                  column={column.column}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  className={column.className}
                >
                  {column.label}
                </SortableTableHead>
              ))}
              {showEvvColumn && (
                <TableHead className='w-24' title='Patient Signature'>
                  <div className='flex items-center gap-1'>
                    <PenLine className='h-3.5 w-3.5' />
                    Pt Sig
                  </div>
                </TableHead>
              )}
              <TableHead>
                <div className='flex items-center gap-1'>
                  <StickyNote className='h-3.5 w-3.5' />
                  Notes
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!clinicsLoaded || (loading && tasks.length === 0) ? (
              <TableRow>
                <TableCell
                  colSpan={tableColumnCount}
                  className='h-32 text-center'
                >
                  <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
                </TableCell>
              </TableRow>
            ) : !loading && tasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={tableColumnCount}
                  className='h-32 text-center text-muted-foreground'
                >
                  {isDateRangeInvalid
                    ? 'From date must be on or before To date.'
                    : debouncedSearch
                      ? 'No tasks match your search.'
                      : statusFilter.length > 0 ||
                          typeFilter.length > 0 ||
                          evvFilter !== 'all'
                        ? 'No tasks match the current filters.'
                        : roleFilter === 'case_manager'
                          ? `No ${
                              qaScope === 'my' ? 'my ' : ''
                            }QA ${emptyStateTaskNoun} found${isAllClinics ? '' : ' for this clinic'}.`
                          : roleFilter === 'clinician'
                            ? `No clinician ${emptyStateTaskNoun} found${isAllClinics ? '' : ' for this clinic'}.`
                            : `No ${emptyStateTaskNoun} found${isAllClinics ? '' : ' for this clinic'}.`}
                </TableCell>
              </TableRow>
            ) : (
              tasks.map(task => {
                const canEditDate = canReschedule && task.is_reschedulable;
                return (
                  <TableRow
                    key={task.id}
                    className='cursor-pointer hover:bg-accent/50 transition-colors'
                    onClick={e => handleRowClick(task, e)}
                  >
                    <TableCell className='font-medium text-sm'>
                      {/*
                      A date that can never move renders as plain text: on a
                      disabled picker the shared trigger's disabled:opacity-50
                      puts the date at 3.7:1. Metrics match the sm picker so
                      the column still reads as one column.

                      The width sits on this wrapper rather than the <th>: the
                      table is auto-layout, so the picker's inner input (~170px
                      intrinsic) would otherwise set the column width.
                    */}
                      <div
                        className='w-[120px]'
                        // The picker owns its own clicks, enabled or not. A
                        // disabled trigger is pointer-events-none, so the click
                        // lands here instead of on the button — without this it
                        // would bubble to the row and open the task, which is
                        // not what clicking an inert control should do.
                        onClick={event => event.stopPropagation()}
                      >
                        {!canEditDate ? (
                          // No aria-label: on a generic element it replaces
                          // the content, so screen readers would announce the
                          // label instead of the date. The Date column header
                          // and row already say which date this is.
                          <span className='flex h-7 items-center px-2 text-xs font-normal'>
                            {formatDateToZeroPaddedMMDDYYYY(task.visit_date)}
                          </span>
                        ) : (
                          <DatePicker
                            key={`${task.id}-${task.visit_date}-${rejectedRescheduleNonces[task.id] ?? 0}`}
                            datePickerSize='sm'
                            // The shared trigger defaults to w-[280px] — wider than
                            // this whole column. Fill the cell instead.
                            className='w-full min-w-0 px-2'
                            date={parseISOToLocalDate(task.visit_date)}
                            onDateChange={date =>
                              void handleRescheduleTask(task, date)
                            }
                            minDate={
                              task.reschedule_min_date
                                ? parseISOToLocalDate(task.reschedule_min_date)
                                : undefined
                            }
                            maxDate={
                              task.reschedule_max_date
                                ? parseISOToLocalDate(task.reschedule_max_date)
                                : undefined
                            }
                            allowClear={false}
                            // Only the in-flight reschedule dims the control now.
                            // That state is transient, and dimming reads as
                            // "busy, hold on" — which is what it means here.
                            disabled={reschedulingTaskIds.has(task.id)}
                            aria-label={`Reschedule ${task.patient_name}`}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className='font-medium text-sm'>
                      {task.patient_name}
                    </TableCell>
                    {showClinicColumn && (
                      <TableCell className='text-sm'>
                        {task.clinic_name || '—'}
                      </TableCell>
                    )}
                    <TableCell className='text-sm'>
                      <span className='flex items-center gap-1.5'>
                        {formatVisitType(task.visit_type)}
                        {task.task_type === 'visit' &&
                          supportsActiveDiagnosisPage(
                            task.visit_type as VisitTypeId
                          ) &&
                          task.has_active_diagnosis && (
                            <Badge
                              variant='secondary'
                              className='shrink-0 text-[10px] px-1.5 py-0'
                              title='Active diagnosis entered'
                            >
                              Dx
                            </Badge>
                          )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={STATUS_BADGE_VARIANT[task.status] ?? 'outline'}
                      >
                        {STATUS_LABEL[task.status] ?? task.status}
                      </Badge>
                    </TableCell>
                    <TableCell className='text-sm text-muted-foreground max-w-xs truncate'>
                      {assignmentLabel(task)}
                    </TableCell>
                    {showEvvColumn && (
                      <TableCell>
                        {task.task_type === 'visit' &&
                        task.is_clinician_task &&
                        canOpenEvvAction(task) ? (
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={event => openEvvAction(task, event)}
                            aria-label={
                              isReviewableEvvTask(task)
                                ? `View Patient Signature for ${task.patient_name}`
                                : `Capture Patient Signature for ${task.patient_name}`
                            }
                          >
                            <PenLine
                              className='h-4 w-4'
                              fill={
                                isReviewableEvvTask(task)
                                  ? 'currentColor'
                                  : 'none'
                              }
                            />
                            {isReviewableEvvTask(task) ? 'View' : 'Capture'}
                          </Button>
                        ) : (
                          <span className='text-sm text-muted-foreground'>
                            —
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className='text-sm text-muted-foreground max-w-xs'>
                      <HotboxNotesCell
                        task={task}
                        onOpen={openTaskNotesDialog}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className='flex items-center justify-between text-sm text-muted-foreground'>
        <span>
          {tasks.length > 0
            ? `Showing ${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + tasks.length}`
            : 'No results'}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className='h-4 w-4' />
            Previous
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
          >
            Next
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>

      <HotboxEvvCaptureDialog
        open={evvCaptureVisitId !== null}
        visitId={evvCaptureVisitId}
        onOpenChange={open => {
          if (!open) setEvvCaptureVisitId(null);
        }}
        onCompleted={() => setEvvRefreshNonce(nonce => nonce + 1)}
      />

      {evvReadVisitId && (
        <EvvReadingsDialog
          open={evvReadVisitId !== null}
          onOpenChange={open => {
            if (!open) setEvvReadVisitId(null);
          }}
          visitId={evvReadVisitId}
          useCaptureView
          canRemove={hasPermission(PermissionSlug.EVV_REMOVE)}
          onRemoved={() => setEvvRefreshNonce(nonce => nonce + 1)}
        />
      )}

      <HotboxTaskNotesDialog
        open={selectedTaskId !== null}
        draftNotes={draftNotes}
        confirmedNotes={confirmedNotes}
        isSaving={isSavingNotes}
        error={taskNotesError}
        onDraftNotesChange={setDraftNotes}
        onSave={handleSaveTaskNotes}
        onCancel={resetTaskNotesDialog}
      />
    </div>
  );
};

export default Hotbox;
